import React, { useState } from 'react';

import EventSeatIcon from '@mui/icons-material/EventSeat';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import ShareIcon from '@mui/icons-material/Share';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));
const StyledPublicIcon = styled(PublicIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginTop: theme.spacing(1)
}));

const StyledLocationOnIcon = styled(LocationOnIcon)(({ theme }) => ({
  marginRight: theme.spacing(1)
}));

const TrainingLocationText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1,
  maxWidth: '13rem',
  [theme.breakpoints.up('md')]: {
    maxWidth: '7rem'
  }
}));

const FooterItem = styled(Box)(({ theme, visible }) => ({
  display: `${visible === 'true' ? 'flex' : 'none'}`,
  color: theme.palette.info.contrastText,
  marginRight: '0.3rem',
  textAlign: 'left',
  lineHeight: 1,
  alignSelf: 'end',
  alignItems: 'center',
  maxWidth: '50%'
}));

const Footer = (props) => {
  const {
    city,
    price,
    currency,
    country,
    config,
    participantStatus = {},
    maxSeats,
    location = ''
  } = props;

  const [isFavorite, setIsFavorite] = useState(false);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  let currencySymbol = null;
  if (price?.length && currency) {
    const priceFormatted = Number().toLocaleString(window.navigator.language, {
      style: 'currency',
      currency: currency
    });

    if (Number.isInteger(parseInt(priceFormatted.substring(0, 1), 10))) {
      currencySymbol = priceFormatted.slice(-1);
    } else {
      currencySymbol = priceFormatted[0];
    }
  }

  return (
    <CardActions>
      <StyledBox>
        {/* Location and City */}
        {location ? (
          <FooterItem visible={config?.footer?.columns?.includes('location').toString()}>
            {config?.footer?.columns?.includes('globeIcon') && (
              <Tooltip title='Global'>
                <StyledPublicIcon />
              </Tooltip>
            )}
            {config?.footer?.columns?.includes('markerIcon') && (
              <Tooltip title='Marker'>
                <StyledLocationOnIcon />
              </Tooltip>
            )}
            <Box>
              <TrainingLocationText>{location}</TrainingLocationText>
              <Typography variant='body2' noWrap>
                {city}
              </Typography>
              <Typography variant='caption' noWrap>
                {country}
              </Typography>
            </Box>
          </FooterItem>
        ) : (
          <div />
        )}

        {/* Price */}
        <FooterItem visible={config?.footer?.columns?.includes('price').toString()}>
          {price && price.length && currency && (
            <Badge
              color='primary'
              badgeContent={price + ' ' + currencySymbol}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            />
          )}
        </FooterItem>

        {/* Progress */}
        <FooterItem visible={config?.footer?.columns?.includes('progress').toString()}>
          <CircularProgress variant='determinate' value={75} size={20} />
          <Typography variant='caption'>75%</Typography>
        </FooterItem>

        {/* Favorite */}
        <FooterItem visible={config?.footer?.columns?.includes('favorite').toString()}>
          <IconButton onClick={handleFavoriteClick} color='primary'>
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </FooterItem>

        {/* Share */}
        <FooterItem visible={config?.footer?.columns?.includes('share').toString()}>
          <IconButton color='primary'>
            <ShareIcon />
          </IconButton>
        </FooterItem>

        {/* Participant Status */}
        <FooterItem visible={config?.footer?.columns?.includes('participantStatus').toString()}>
          <Typography variant='caption'>{participantStatus?.value}</Typography>
        </FooterItem>

        {/* Max Seats */}
        <FooterItem visible={config?.footer?.columns?.includes('maxSeats').toString()}>
          {maxSeats ? (
            <Typography sx={{ textAlign: 'right' }} variant='body2'>
              {maxSeats}
            </Typography>
          ) : null}
          {maxSeats ? <EventSeatIcon sx={{ ml: 0.8 }} /> : null}
        </FooterItem>
      </StyledBox>
    </CardActions>
  );
};

export default Footer;
